import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CreditCard, Loader2, FileText, AlertCircle, CheckCircle2, Lock, Landmark, ThumbsUp, MessageCircle, X as XIcon } from 'lucide-react';
import ProductDisplayCard, { groupItemsByMethod, MethodSectionHeader } from '../components/ProductDisplayCard';
import VectorDesignEditor from '../components/VectorDesignEditor';
import LiveMockupCanvas from '../components/LiveMockupCanvas';
import CardBrandStrip from '../components/CardBrandStrip';
import { ZoomableImage } from '../components/MockupLightbox';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

// The secure share token travels on every public API call for this page.
const shareToken = () => new URLSearchParams(window.location.search).get('t') || '';
axios.interceptors.request.use((config) => {
  const t = shareToken();
  if (t && window.location.pathname.startsWith('/pay/')) config.headers['X-Share-Token'] = t;
  return config;
});

const PublicPayment = () => {
  const { proposalId } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const [resent, setResent] = useState(false);

  const resendLink = async () => {
    try { await axios.post(`/proposals/public/${proposalId}/resend-link`); } catch { /* generic */ }
    setResent(true);
  };

  // Refs for values used inside Collect.js callbacks (stale closure protection)
  const proposalRef = useRef(null);
  const billingRef = useRef({});

  // Tabs: 'card' | 'ach'
  const [method, setMethod] = useState('card');

  // Billing fields (used for both card and ACH)
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address1, setAddress1] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');

  // ACH fields
  const [achName, setAchName] = useState('');
  const [achRouting, setAchRouting] = useState('');
  const [achAccount, setAchAccount] = useState('');
  const [achHolderType, setAchHolderType] = useState('personal');
  const [achAcctType, setAchAcctType] = useState('checking');
  const [achAuthorized, setAchAuthorized] = useState(false);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successData, setSuccessData] = useState(null);

  // Public "Accept without payment" — only available when proposal.can_skip_payment is true
  const [accepting, setAccepting] = useState(false);
  const [acceptedData, setAcceptedData] = useState(null);
  const [accepterName, setAccepterName] = useState('');

  // Per-item mockup approval state
  const [respondingIdx, setRespondingIdx] = useState(null); // index currently submitting
  const [changesItemIdx, setChangesItemIdx] = useState(null); // index whose "request changes" form is open
  const [changesFeedback, setChangesFeedback] = useState('');

  const refreshProposal = useCallback(async () => {
    try {
      const r = await axios.get(`/proposals/public/${proposalId}`);
      setProposal(r.data);
    } catch {
      /* ignore */
    }
  }, [proposalId]);

  const respondToMockup = async (itemIndex, approved, feedback = '') => {
    setRespondingIdx(itemIndex);
    try {
      await axios.post(
        `/proposals/public/${proposalId}/items/${itemIndex}/respond`,
        { approved, feedback, accepter_name: accepterName || proposal?.customer_name || null },
      );
      await refreshProposal();
      setChangesItemIdx(null);
      setChangesFeedback('');
    } catch (e) {
      setSubmitError(e.response?.data?.detail || 'Could not submit your response');
    } finally {
      setRespondingIdx(null);
    }
  };

  // Collect.js state
  const [collectReady, setCollectReady] = useState(false);

  // Payment amount selection: 'full' (outstanding balance) | 'deposit' (admin-authorized amount only).
  // Customers can never enter their own amount.
  const [amountChoice, setAmountChoice] = useState('full');
  const amountChoiceRef = useRef('full');

  useEffect(() => { amountChoiceRef.current = amountChoice; }, [amountChoice]);

  // ---- Load proposal + Collect.js ----
  useEffect(() => {
    const fetchProposal = async () => {
      try {
        const res = await axios.get(`/proposals/public/${proposalId}`);
        setProposal(res.data);
        proposalRef.current = res.data;
        setEmail(res.data?.customer_email || '');
        const fullName = res.data?.customer_name || '';
        const [fn, ...rest] = fullName.split(' ');
        setFirstName(fn || '');
        setLastName(rest.join(' '));
        // Default to the admin-authorized deposit when one is offered, otherwise the full balance.
        const depositOffered = Number(res.data?.partial_payment?.amount_due_now || 0) > 0;
        setAmountChoice(depositOffered ? 'deposit' : 'full');
      } catch (e) {
        if (e.response?.status === 403) setLinkInvalid(true);
        setPageError(e.response?.data?.detail || 'Proposal not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProposal();
  }, [proposalId]);

  // Keep billing ref synced so Collect.js callback sees latest values
  useEffect(() => {
    billingRef.current = {
      email, firstName, lastName, address1, city, state, zip,
    };
  }, [email, firstName, lastName, address1, city, state, zip]);

  // Configure Collect.js (script already loaded from index.html)
  const configureCollect = useCallback(() => {
    if (!window.CollectJS) return;
    try {
      window.CollectJS.configure({
        variant: 'inline',
        fields: {
          ccnumber: { selector: '#stares-card-num', placeholder: '1234 5678 9012 3456' },
          ccexp: { selector: '#stares-card-exp', placeholder: 'MM / YY' },
          cvv: { selector: '#stares-card-cvv', placeholder: 'CVV' },
        },
        customCss: {
          'height': '38px',
          'width': '100%',
          'padding': '0 12px',
          'font-size': '15px',
          'color': '#111827',
          'border': 'none',
          'outline': 'none',
          'background-color': '#ffffff',
          'box-sizing': 'border-box',
        },
        callback: (response) => {
          if (response && response.token) {
            submitCardPayment(response.token, response.card || null);
          } else {
            setSubmitting(false);
            // Surface the actual Collect.js error instead of a generic "try
            // again" message — customers and Stares Group staff alike were
            // staring at a useless toast and couldn't tell whether the issue
            // was CVV, AVS, expiration, or a 3DS challenge being blocked.
            const reason =
              response?.error ||
              response?.errorMessage ||
              response?.message ||
              response?.tokenError ||
              null;
            console.warn('[Collect.js] tokenization failed:', response);
            setSubmitError(
              reason
                ? `Card couldn't be processed: ${reason}`
                : "Card details couldn't be tokenized — double-check the number, expiration, CVV, and ZIP code. If everything looks right, the bank may be blocking the charge; try a different card.",
            );
          }
        },
      });
      // Mark ready immediately so the user can interact. Collect.js mounts the
      // iframes synchronously after configure() and they are clickable right away.
      setCollectReady(true);
    } catch (e) {
      console.error('Collect.js configure error:', e);
      setSubmitError('Payment form configuration failed');
    }
  }, []);

  // Wait for BOTH Collect.js to load AND the card form divs to exist in DOM,
  // then configure. Critical: configuring before the #stares-card-* divs exist
  // makes Collect.js silently fail and never mount the iframes.
  useEffect(() => {
    if (loading || !proposal) return; // wait for proposal to finish loading
    let cancelled = false;
    const wait = setInterval(() => {
      if (cancelled) return;
      const hasDivs = document.getElementById('stares-card-num')
        && document.getElementById('stares-card-exp')
        && document.getElementById('stares-card-cvv');
      if (window.CollectJS && hasDivs) {
        clearInterval(wait);
        configureCollect();
      }
    }, 100);
    const timeout = setTimeout(() => {
      clearInterval(wait);
      if (!window.CollectJS && !cancelled) setSubmitError('Payment form failed to load');
    }, 12000);
    return () => { cancelled = true; clearInterval(wait); clearTimeout(timeout); };
  }, [loading, proposal, configureCollect]);

  // Accept proposal without payment — only available when the backend marks
  // the proposal as `can_skip_payment` (currently FIU customers).
  const acceptWithoutPayment = async () => {
    if (!proposal) return;
    const name = (accepterName || proposal.customer_name || '').trim();
    if (!name) {
      setSubmitError('Please type your name to confirm you accept this proposal.');
      return;
    }
    if (!window.confirm(
      `Accept proposal ${proposal.proposal_number} for ${formatCurrency(proposal.total_amount)} without payment?\n\nWe'll send the order straight into production.`,
    )) return;
    setAccepting(true);
    setSubmitError(null);
    try {
      const res = await axios.post(`/proposals/public/${proposalId}/accept`, {
        accepted_name: name,
      });
      setAcceptedData(res.data);
    } catch (e) {
      setSubmitError(e.response?.data?.detail || `Could not accept proposal — please contact ${brandName()}`);
    } finally {
      setAccepting(false);
    }
  };

  // Compute the dollar amount to actually charge. Only two possibilities: the admin-authorized deposit
  // (server-computed, never editable here) or the full outstanding balance.
  const computePayAmount = (proposalData, choice) => {
    if (!proposalData) return 0;
    const balance = Number(proposalData.balance_due ?? proposalData.total_amount ?? 0);
    const depositNow = Number(proposalData.partial_payment?.amount_due_now || 0);
    if (choice === 'deposit' && depositNow > 0) return Math.min(depositNow, balance);
    return balance;
  };

  // 3% Credit Card processing surcharge — applied ONLY when paying by card,
  // not on ACH. Quoted as a separate line on the payment summary for
  // transparency (some states require it disclosed).
  const CC_SURCHARGE_RATE = 0.03;
  const computeCardSurcharge = (baseAmount, payMethod) => {
    if (payMethod !== 'card') return 0;
    const b = Number(baseAmount) || 0;
    return Math.round(b * CC_SURCHARGE_RATE * 100) / 100;
  };
  const computeTotalCharge = (baseAmount, payMethod) =>
    Math.round((Number(baseAmount || 0) + computeCardSurcharge(baseAmount, payMethod)) * 100) / 100;

  const submitCardPayment = async (paymentToken, cardMeta) => {
    try {
      const p = proposalRef.current;
      const b = billingRef.current;
      if (!p) {
        setSubmitting(false);
        setSubmitError('Order details not loaded — please refresh and try again');
        return;
      }
      const baseAmount = computePayAmount(p, amountChoiceRef.current);
      if (baseAmount <= 0) {
        setSubmitting(false);
        setSubmitError('Please enter a valid amount to pay');
        return;
      }
      const surcharge = computeCardSurcharge(baseAmount, 'card');
      const amountToCharge = computeTotalCharge(baseAmount, 'card');
      const res = await axios.post('/inspyre/pay', {
        proposal_id: proposalId,
        amount: amountToCharge,
        base_amount: baseAmount,
        cc_surcharge: surcharge,
        customer_email: b.email,
        payment_token: paymentToken,
        card_last4: cardMeta?.number ? String(cardMeta.number).slice(-4) : null,
        card_type: cardMeta?.type || null,
        billing_address: {
          first_name: b.firstName, last_name: b.lastName,
          address1: b.address1, city: b.city, state: b.state,
          postal_code: b.zip, country: 'US',
        },
      });
      handleResponse(res.data);
    } catch (e) {
      setSubmitting(false);
      setSubmitError(e.response?.data?.detail || e.message || 'Payment failed');
    }
  };

  const handleResponse = (data) => {
    setSubmitting(false);
    if (data.status === 'success') {
      setSuccessData(data);
    } else {
      setSubmitError(data.message || 'Payment was not approved');
    }
  };

  const handleCardSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!window.CollectJS) {
      setSubmitError('Payment form not ready, please refresh');
      return;
    }
    setSubmitting(true);
    // This triggers tokenization → callback → submitCardPayment
    window.CollectJS.startPaymentRequest();
  };

  const handleAchSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!achAuthorized) {
      setSubmitError('Please authorize the ACH debit by checking the box');
      return;
    }
    const amountToCharge = computePayAmount(proposal, amountChoice);
    if (amountToCharge <= 0) {
      setSubmitError('Please enter a valid amount to pay');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post('/inspyre/pay', {
        proposal_id: proposalId,
        amount: amountToCharge,
        customer_email: email,
        checkname: achName,
        checkaba: achRouting,
        checkaccount: achAccount,
        account_holder_type: achHolderType,
        account_type: achAcctType,
        sec_code: 'WEB',
        billing_address: {
          first_name: firstName, last_name: lastName,
          address1, city, state, postal_code: zip, country: 'US',
        },
      });
      handleResponse(res.data);
    } catch (err) {
      setSubmitting(false);
      setSubmitError(err.response?.data?.detail || err.message || 'Payment failed');
    }
  };

  // ---- UI ----
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <Loader2 className="w-12 h-12 animate-spin text-red-500" />
      </div>
    );
  }
  if (pageError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 p-6">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center" data-testid="public-link-error">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">{linkInvalid ? 'This link needs a refresh' : 'Error'}</h1>
          <p className="text-gray-600">{linkInvalid ? 'For your security, proposal links are personal and can expire. We can email you a fresh secure link.' : pageError}</p>
          {linkInvalid && !resent && (
            <Button onClick={resendLink} className="mt-6 w-full" data-testid="resend-link-btn">Email me a new link</Button>
          )}
          {resent && <p className="mt-6 text-sm text-green-700" data-testid="resend-link-sent">If this proposal exists, a new link is on its way to the email on file.</p>}
        </div>
      </div>
    );
  }

  const isPaid = proposal?.status === 'paid';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-red-950 py-10 px-4"
      data-testid="public-payment-page">
      {/* Force Collect.js iframes to fill their host containers — without this the iframes
         default to 300×150 and inputs become unclickable / cursor never appears. */}
      <style>{`
        #stares-card-num iframe,
        #stares-card-exp iframe,
        #stares-card-cvv iframe {
          width: 100% !important;
          height: 100% !important;
          display: block !important;
          border: 0 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        #stares-card-num, #stares-card-exp, #stares-card-cvv { overflow: visible; }
      `}</style>
      <div className="max-w-2xl mx-auto">
        {/* Brand header */}
        <div className="text-center mb-8">
          <h1 className="text-white text-3xl font-bold tracking-tight">STARES GROUP</h1>
          <p className="text-gray-400 text-sm">Custom Apparel · Secure Checkout</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Proposal summary */}
          <div className="bg-gradient-to-r from-gray-900 to-red-700 text-white p-6">
            <FileText className="w-10 h-10 mb-2 opacity-90" />
            <div className="text-lg font-semibold">Quote for {proposal.customer_name}</div>
            <div className="text-sm opacity-80">
              {proposal.order_number ? `Order #${proposal.order_number}` : `Proposal #${proposal.proposal_number}`}
            </div>
          </div>

          {isPaid && (
            <div className="bg-green-50 border-b border-green-200 px-6 py-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <span className="text-green-800 font-medium">This proposal has been paid.</span>
            </div>
          )}

          {successData && (
            <div className="bg-green-50 border-b border-green-200 px-6 py-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
                <span className="text-green-800 font-bold text-lg">
                  {successData.is_fully_paid ? 'Payment Complete' : 'Partial Payment Received'}
                </span>
              </div>
              <p className="text-sm text-green-700">
                Charged <strong>{formatCurrency(successData.amount_charged)}</strong> · Transaction ID:{' '}
                <span className="font-mono">{successData.transactionid}</span>
              </p>
              {!successData.is_fully_paid && (
                <p className="text-sm text-amber-700 mt-2">
                  Remaining balance: <strong>{formatCurrency(successData.balance_due)}</strong> — you can return to this page anytime to pay the balance.
                </p>
              )}
              {successData.is_fully_paid && (
                <p className="text-sm text-green-700">
                  Your order is being processed. A receipt has been emailed to you.
                </p>
              )}
            </div>
          )}

          {/* Shipped banner — carrier + tracking link for the customer */}
          {proposal.shipping?.tracking_number && (
            <div className="mx-6 mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="public-shipping-banner">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-1">
                📦 Your Order Has Shipped
              </div>
              <div className="text-sm text-gray-800 font-medium">
                {proposal.shipping.carrier} — {proposal.shipping.service}
              </div>
              <div className="text-sm mt-1">
                Tracking:{' '}
                {proposal.shipping.tracking_url ? (
                  <a href={proposal.shipping.tracking_url} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold underline break-all" data-testid="public-tracking-link">
                    {proposal.shipping.tracking_number}
                  </a>
                ) : (
                  <span className="font-semibold">{proposal.shipping.tracking_number}</span>
                )}
              </div>
            </div>
          )}

          {/* Full Order Review — items with mockup thumbnails, sizes,
              decoration spec. Per-item prices intentionally hidden — client
              sees only the grand total + balance breakdown below. */}
          <div className="p-6 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                Order Review · {proposal.items?.length || 0} item{(proposal.items?.length || 0) === 1 ? '' : 's'}
                {proposal.quantity ? ` · ${proposal.quantity} total pieces` : ''}
              </div>
              <div className="space-y-3" data-testid="public-items-list">
                {(() => {
                  const groups = groupItemsByMethod(proposal.items || []);
                  const showHeaders = groups.length > 1;
                  return groups.map((group) => (
                    <div key={group.label} className="space-y-3"
                      data-testid={`public-method-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      {showHeaders && (
                        <MethodSectionHeader
                          label={group.label}
                          itemCount={group.items.length}
                          totalPieces={group.totalPieces}
                          totalAmount={group.totalAmount}
                          showAmount={false}
                        />
                      )}
                      {group.items.map((item) => {
                        const i = item._origIndex;
                        return (
                    <div key={i} className="space-y-3" data-testid={`public-item-${i}`}>
                      {/* Polished product card (no Setup Fee — client-facing).
                          allInPricing=true rolls print cost into the per-item
                          $X.XX/each so the line total matches what the customer
                          expects against the grand total. Setup fees + tax still
                          render separately below in the totals block. */}
                      <ProductDisplayCard item={item} isAdmin={false} allInPricing={true} />

                      {/* Customer-side context: prior feedback note */}
                      {item.approval_status === 'changes_requested' && item.approval_feedback && (
                        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                          <strong>Your previous note:</strong> {item.approval_feedback}
                        </div>
                      )}

                        {/* Mockup Approval Block — only renders when the item
                            actually has a mockup. Otherwise this item doesn't
                            need a sign-off and we keep the card clean. */}
                        {item.has_mockup && (
                          <div className="mt-3 p-3 rounded-lg border-2"
                            style={{
                              background: item.approval_status === 'approved' ? '#f0fdf4'
                                : item.approval_status === 'changes_requested' ? '#fffbeb'
                                : '#fef2f2',
                              borderColor: item.approval_status === 'approved' ? '#bbf7d0'
                                : item.approval_status === 'changes_requested' ? '#fde68a'
                                : '#fecaca',
                            }}
                            data-testid={`mockup-approval-block-${i}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] font-bold uppercase tracking-wider"
                                style={{
                                  color: item.approval_status === 'approved' ? '#166534'
                                    : item.approval_status === 'changes_requested' ? '#92400e'
                                    : '#991b1b',
                                }}>
                                Mockup Approval
                              </span>
                              <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                style={{
                                  background: item.approval_status === 'approved' ? '#16a34a'
                                    : item.approval_status === 'changes_requested' ? '#f59e0b'
                                    : '#dc2626',
                                  color: 'white',
                                }}
                                data-testid={`mockup-approval-status-${i}`}>
                                {item.approval_status === 'approved' ? '✓ APPROVED'
                                  : item.approval_status === 'changes_requested' ? '✏ CHANGES REQUESTED'
                                  : '⚠ NOT YET APPROVED'}
                              </span>
                            </div>

                            {/* Mockup gallery — show every saved side (Front,
                                Back, Left, Right + any custom) so the customer
                                can verify the design from all angles before
                                approving. Falls back to the legacy single
                                `mockup_url` when no `mockup_views` map exists. */}
                            {(() => {
                              const composites = item.mockup_views || {};
                              const garment = item.garment_views || {};
                              const unwrap = (u) => {
                                if (!u || typeof u !== 'string') return u;
                                const m = u.match(/(?:^|https?:\/\/[^/]+)\/api\/img-proxy\?url=(.+)$/);
                                if (m) { try { return decodeURIComponent(m[1]); } catch { return u; } }
                                return u;
                              };
                              const orderedKeys = ['front', 'back', 'left', 'right'];
                              // Merge: printed composite wins; otherwise show the blank
                              // garment photo for that angle so the customer can review
                              // ALL sides. Skip non-front garment photos that are just a
                              // duplicate of the front fallback image.
                              const frontGarment = unwrap(garment.front);
                              const views = {};
                              orderedKeys.forEach((k) => {
                                if (composites[k]) { views[k] = composites[k]; return; }
                                const g = unwrap(garment[k]);
                                if (g && (k === 'front' || g !== frontGarment)) views[k] = g;
                              });
                              Object.keys(composites).forEach((k) => {
                                if (!views[k] && composites[k]) views[k] = composites[k];
                              });
                              const sizes = item.print_sizes || {};
                              const allKeys = [
                                ...orderedKeys.filter((k) => views[k]),
                                ...Object.keys(views).filter((k) => !orderedKeys.includes(k) && views[k]),
                              ];
                              const hasViews = allKeys.length > 0;
                              const fallback = item.mockup_base64 || item.mockup_url || item.design_file || item.image_url;
                              if (!hasViews && !fallback) return null;
                              const labelFor = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                              const sizeFor = (k) => sizes[k.toLowerCase()] || sizes[k] || '';
                              return (
                                <div
                                  data-testid={`mockup-gallery-${i}`}
                                  className="mt-2 mb-3 grid gap-3"
                                  style={{
                                    gridTemplateColumns: hasViews && allKeys.length > 1
                                      ? `repeat(${Math.min(allKeys.length, 4)}, minmax(0, 1fr))`
                                      : '1fr',
                                  }}
                                >
                                  {hasViews
                                    ? allKeys.map((k) => (
                                        <div
                                          key={k}
                                          data-testid={`mockup-view-${i}-${k}`}
                                          className="block bg-white rounded-md border border-gray-200 overflow-hidden hover:border-gray-400 transition-colors"
                                        >
                                          <ZoomableImage
                                            src={views[k]}
                                            alt={`${labelFor(k)} view`}
                                            title={`${labelFor(k)} mockup`}
                                            style={{
                                              width: '100%',
                                              aspectRatio: '1 / 1',
                                              background: '#ffffff',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              overflow: 'hidden',
                                            }}
                                            imgStyle={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                            testId={`zoomable-public-view-${i}-${k}`}
                                          />
                                          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 text-center py-1 bg-gray-50">
                                            {labelFor(k)}
                                            {sizeFor(k) && (
                                              <span
                                                data-testid={`mockup-size-${i}-${k}`}
                                                className="block text-[10px] font-bold normal-case tracking-normal text-gray-900 mt-0.5"
                                              >
                                                Logo: {sizeFor(k)}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))
                                    : (
                                      <div
                                        data-testid={`mockup-view-${i}-fallback`}
                                        className="block bg-white rounded-md border border-gray-200 overflow-hidden hover:border-gray-400 transition-colors"
                                      >
                                        <ZoomableImage
                                          src={fallback}
                                          alt="Design mockup"
                                          title="Design mockup"
                                          style={{
                                            width: '100%',
                                            maxHeight: '320px',
                                            background: '#ffffff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                          }}
                                          imgStyle={{ maxWidth: '100%', maxHeight: '320px', objectFit: 'contain' }}
                                          testId={`zoomable-public-fallback-${i}`}
                                        />
                                      </div>
                                    )}
                                </div>
                              );
                            })()}
                              {item.approval_status === 'approved' && item.approved_at && (
                                <div className="text-[10px] text-green-700 mb-2">
                                  Approved {new Date(item.approved_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                </div>
                              )}

                            {/* Action buttons — only when not yet approved */}
                            {item.approval_status !== 'approved' && !acceptedData && (
                              changesItemIdx === i ? (
                                <div className="space-y-2 mt-2">
                                  <textarea
                                    value={changesFeedback}
                                    onChange={(e) => setChangesFeedback(e.target.value)}
                                    placeholder="Tell us what to change (e.g. make the logo bigger, swap the gold for white)…"
                                    rows={2}
                                    className="w-full text-xs p-2 border border-amber-300 rounded"
                                    data-testid={`changes-feedback-${i}`}
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      onClick={() => respondToMockup(i, false, changesFeedback)}
                                      disabled={respondingIdx === i || !changesFeedback.trim()}
                                      size="sm"
                                      className="bg-amber-600 hover:bg-amber-700 text-white"
                                      data-testid={`submit-changes-${i}`}>
                                      {respondingIdx === i ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                                      Submit Change Request
                                    </Button>
                                    <Button
                                      onClick={() => { setChangesItemIdx(null); setChangesFeedback(''); }}
                                      size="sm" variant="ghost"
                                      data-testid={`cancel-changes-${i}`}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex gap-2 mt-2">
                                  <Button
                                    onClick={() => respondToMockup(i, true)}
                                    disabled={respondingIdx === i}
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    data-testid={`approve-mockup-${i}`}>
                                    {respondingIdx === i
                                      ? <><Loader2 size={12} className="animate-spin mr-1" />Approving…</>
                                      : <><ThumbsUp size={12} className="mr-1" />Approve this design</>}
                                  </Button>
                                  <Button
                                    onClick={() => { setChangesItemIdx(i); setChangesFeedback(''); }}
                                    size="sm" variant="outline"
                                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                                    data-testid={`request-changes-${i}`}>
                                    <MessageCircle size={12} className="mr-1" />Request changes
                                  </Button>
                                </div>
                              )
                            )}
                          </div>
                        )}

                        {/* Vector design (SVG) — customer can recolor swatches
                            AND drag/scale/rotate the design directly on the
                            live mockup. Lives outside the approval block so it
                            stays visible after approval (locked once approved).
                            Backend rejects edits when item.approval_status ===
                            'approved' or proposal.status === 'paid'. */}
                        {item.vector_design && (() => {
                          const vd = item.vector_design;
                          const isSvg = vd.format === 'svg';
                          const placements = vd.placements || {};
                          // Pair each placement with the matching mockup view
                          // image (if the admin has generated one). Locations
                          // that don't have a base image still render — over
                          // a plain background — which is fine: the customer
                          // sees the layout intent.
                          const mockupSrcFor = (loc) =>
                            (item.mockup_views && (
                              item.mockup_views[loc] ||
                              item.mockup_views[loc.replace('_sleeve', '')] ||
                              item.mockup_views[loc.replace('_', '')]
                            )) || null;
                          const locations = Object.keys(placements).length
                            ? Object.keys(placements)
                            : ['front'];
                          const isLocked =
                            item.approval_status === 'approved' ||
                            proposal.status === 'paid';
                          const patchPublic = async (body) => {
                            const r = await axios.patch(
                              `/proposals/public/${proposal.id}/items/${i}/vector-design`,
                              body
                            );
                            return r.data.vector_design;
                          };
                          const onPlacementChange = async (loc, next) => {
                            try {
                              const updated = await patchPublic({ placements: { [loc]: next } });
                              setProposal((prev) => {
                                if (!prev) return prev;
                                const items = [...(prev.items || [])];
                                items[i] = { ...items[i], vector_design: updated };
                                return { ...prev, items };
                              });
                            } catch (e) {
                              /* toast handled inside editor for color paths; placement errors
                                 are quietly logged so the slider stays responsive */
                              console.warn('Placement save failed', e);
                            }
                          };
                          return (
                            <div className="mt-3 space-y-3" data-testid={`vector-design-block-${i}`}>
                              {/* Instructions card — gently teach the customer
                                  what they can do with the live mockup. */}
                              <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3 text-sm">
                                <p className="font-semibold text-indigo-900 mb-1">
                                  Customize your design
                                </p>
                                <ul className="list-disc pl-5 text-indigo-800 text-xs space-y-0.5">
                                  <li><strong>Drag</strong> the design on the t-shirt to move it.</li>
                                  <li>Use the <strong>Size</strong> and <strong>Rotation</strong> sliders below the preview.</li>
                                  <li>Click any color swatch to <strong>change its color</strong>.</li>
                                  <li>When you&apos;re happy, click <strong>Approve this design</strong> below — then you can pay.</li>
                                </ul>
                                {isLocked && (
                                  <p className="text-[11px] text-indigo-700 mt-2 italic">
                                    This design is locked. Email us at {brandEmail()} to make changes.
                                  </p>
                                )}
                              </div>

                              {isSvg && (
                                <div
                                  className="grid gap-3"
                                  style={{
                                    gridTemplateColumns: locations.length > 1
                                      ? 'repeat(auto-fit, minmax(220px, 1fr))'
                                      : '1fr',
                                  }}
                                >
                                  {locations.map((loc) => (
                                    <LiveMockupCanvas
                                      key={loc}
                                      baseSrc={mockupSrcFor(loc)}
                                      svgContent={vd.svg_content}
                                      placement={placements[loc] || { x: 50, y: 35, scale: 25, rotation: 0 }}
                                      locationLabel={loc.replace(/_/g, ' ')}
                                      editable={!isLocked}
                                      onPlacementChange={(next) => onPlacementChange(loc, next)}
                                      testIdPrefix={`live-mockup-${i}-${loc}`}
                                    />
                                  ))}
                                </div>
                              )}

                              <VectorDesignEditor
                                vectorDesign={vd}
                                proposalId={proposal.id}
                                itemIndex={i}
                                mode="public"
                                locations={[]}
                                locked={isLocked}
                                inlineApi={{ patch: patchPublic }}
                                onUpdate={(updated) => {
                                  setProposal((prev) => {
                                    if (!prev) return prev;
                                    const items = [...(prev.items || [])];
                                    items[i] = { ...items[i], vector_design: updated };
                                    return { ...prev, items };
                                  });
                                }}
                              />
                            </div>
                          );
                        })()}
                    </div>
                  );
                      })}
                    </div>
                  ));
                })()}
              </div>
              {proposal.notes && (
                <div className="mt-3 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap">
                  <strong className="text-gray-800">Notes:</strong> {proposal.notes}
                </div>
              )}
            </div>

            {/* Balance breakdown */}
            <div className="bg-gray-900 text-white rounded-lg p-4 space-y-2">
              {/* Gross subtotal (garments + print + setup + additional, pre-discount).
                  Only shown when a discount is being applied, so the customer
                  sees the "before" number they were quoted. */}
              {Number(proposal.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-sm text-gray-400" data-testid="public-gross-subtotal-line">
                  <span>Subtotal (before discount)</span>
                  <span>
                    {formatCurrency(
                      Number(proposal.shirt_total || 0) +
                      Number(proposal.print_total || 0) +
                      Number(proposal.setup_fees || 0) +
                      Number(proposal.additional_fees || 0)
                    )}
                  </span>
                </div>
              )}
              {/* Discount row — visible whenever the proposal has a non-zero
                  discount_amount so customers see the savings you gave them. */}
              {Number(proposal.discount_amount || 0) > 0 && (
                <div className="flex justify-between text-sm text-emerald-400" data-testid="public-discount-line">
                  <span>Discount</span>
                  <span>− {formatCurrency(proposal.discount_amount)}</span>
                </div>
              )}
              {/* Subtotal (garments + print + setup, pre-tax). Shown when tax
                  is present so the customer can see the math from line totals
                  → subtotal → tax → grand. */}
              {Number(proposal.tax_amount || 0) > 0 && Number(proposal.subtotal || 0) > 0 && (
                <div className="flex justify-between text-sm text-gray-300" data-testid="public-subtotal-line">
                  <span>Subtotal</span>
                  <span>{formatCurrency(proposal.subtotal)}</span>
                </div>
              )}
              {Number(proposal.tax_amount || 0) > 0 && (
                <div className="flex justify-between text-sm text-gray-300" data-testid="public-tax-line">
                  <span>Sales Tax{proposal.tax_rate ? ` (${(Number(proposal.tax_rate) * 100).toFixed(2).replace(/\.?0+$/, '')}%)` : ''}</span>
                  <span>{formatCurrency(proposal.tax_amount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-gray-300">
                <span>Order Total</span>
                <span>{formatCurrency(proposal.total_amount)}</span>
              </div>
              {(proposal.amount_paid > 0) && (
                <div className="flex justify-between text-sm text-green-400">
                  <span>Already Paid ({proposal.payments_count} payment{proposal.payments_count > 1 ? 's' : ''})</span>
                  <span>− {formatCurrency(proposal.amount_paid)}</span>
                </div>
              )}
              <div className="border-t border-white/15 pt-2 flex justify-between items-center">
                <span className="text-lg font-semibold">Balance Due</span>
                <span className="text-3xl font-bold text-red-400" data-testid="balance-due">
                  {formatCurrency(proposal.balance_due)}
                </span>
              </div>
              <div className="text-xs text-gray-400 pt-1" data-testid="public-cc-fee-note">
                Pay by ACH/bank → no extra fees. Pay by credit card → a 3% processing fee will be added when you select that method below.
              </div>
            </div>

            {/* Accepted (no-payment) success state */}
            {acceptedData && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-5 text-center"
                data-testid="accepted-no-payment-banner">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <div className="text-lg font-bold text-green-800 mb-1">
                  Proposal Accepted — Thank You!
                </div>
                <p className="text-sm text-green-700">
                  We've received your acceptance. Your order is being sent to production. You'll
                  receive a confirmation email shortly.
                </p>
              </div>
            )}

            {/* Pending-mockups warning — applies to ALL acceptance paths */}
            {proposal.any_design_present && proposal.pending_mockup_count > 0 && !acceptedData && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 text-sm text-red-900"
                data-testid="pending-mockups-warning">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold mb-1">
                      {proposal.pending_mockup_count === 1
                        ? '1 design still needs your approval'
                        : `${proposal.pending_mockup_count} designs still need your approval`}
                    </div>
                    <p>
                      Please review and approve every mockup above before accepting the proposal.
                      You can request changes if anything looks off.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No-payment-required bypass — FIU (legacy) or an admin-approved payment-terms customer. */}
            {proposal.can_skip_payment && !acceptedData && !isPaid && proposal.balance_due > 0 && (
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-5"
                data-testid="fiu-skip-panel">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    {proposal.is_fiu ? 'FIU' : <CheckCircle2 className="w-5 h-5" />}
                  </div>
                  <div>
                    <div className="font-bold text-blue-900" data-testid="skip-payment-label">
                      {proposal.skip_payment_label || 'No Payment Required'}
                    </div>
                    <p className="text-sm text-blue-800 mt-1">
                      {proposal.is_fiu
                        ? 'As an FIU customer, you can accept this proposal without paying now — your department will be invoiced through your normal channels.'
                        : `Your account has approved payment terms with ${brandName()} — you can accept this proposal now and we will invoice you.`}
                      {proposal.any_design_present && (
                        <> By clicking accept you are <strong>also approving every mockup shown above</strong>.</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-blue-900">Your name (for our records)</Label>
                  <Input
                    type="text"
                    value={accepterName}
                    onChange={(e) => setAccepterName(e.target.value)}
                    placeholder={proposal.customer_name || 'Full name'}
                    data-testid="accepter-name-input"
                  />
                  <Button
                    onClick={acceptWithoutPayment}
                    disabled={accepting}
                    className="w-full bg-green-600 hover:bg-green-700 text-white text-base py-6"
                    data-testid="accept-without-payment-btn">
                    {accepting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting…</>
                    ) : (
                      <><CheckCircle2 className="w-5 h-5 mr-2" />
                        {proposal.any_design_present
                          ? 'Accept Proposal + Approve Mockups · No Payment'
                          : 'Accept Proposal · No Payment'}
                      </>
                    )}
                  </Button>
                </div>
                <div className="text-center text-xs text-blue-700 mt-3">
                  — or pay below if you'd prefer to settle now —
                </div>
              </div>
            )}

            {!isPaid && !successData && proposal.balance_due > 0 && (
              <>
                {/* Payment amount selector — full balance, plus the admin-authorized deposit when enabled. */}
                {(() => {
                  const depositNow = Number(proposal.partial_payment?.amount_due_now || 0);
                  const depositOffered = depositNow > 0 && depositNow < proposal.balance_due;
                  const visible = [
                    ...(depositOffered ? [{ v: 'deposit', label: 'Pay Deposit', sub: `${formatCurrency(depositNow)} · authorized by ${brandName()}` }] : []),
                    { v: 'full', label: depositOffered || proposal.amount_paid > 0 ? 'Pay Full Balance' : 'Pay in Full', sub: formatCurrency(proposal.balance_due) },
                  ];
                  if (visible.length <= 1) {
                    // Only "Pay in Full" — no selector needed, just show what they're paying.
                    return (
                      <div className="space-y-2" data-testid="amount-selector">
                        <div className="text-xs uppercase tracking-wider text-gray-500">Amount due today</div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700" data-testid="amount-due-full">
                          {proposal.amount_paid > 0 ? 'Remaining balance due' : 'Paying today'}: <strong>{formatCurrency(proposal.balance_due)}</strong>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="space-y-2" data-testid="amount-selector">
                      <div className="text-xs uppercase tracking-wider text-gray-500">How much to pay today</div>
                      <div className="grid grid-cols-2 gap-2">
                        {visible.map(opt => (
                          <button key={opt.v} type="button"
                            onClick={() => setAmountChoice(opt.v)}
                            className={`p-3 rounded-lg border-2 text-left transition ${
                              amountChoice === opt.v
                                ? 'border-red-600 bg-red-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            data-testid={`amount-option-${opt.v}`}>
                            <div className="text-sm font-semibold text-gray-900">{opt.label}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{opt.sub}</div>
                          </button>
                        ))}
                      </div>
                      {/* Show the effective amount being charged */}
                      {(() => {
                        const eff = computePayAmount(proposal, amountChoice);
                        const surcharge = computeCardSurcharge(eff, method);
                        const totalCharge = computeTotalCharge(eff, method);
                        return (
                          <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3 text-amber-900 space-y-1">
                            <div className="flex justify-between">
                              <span>Subtotal:</span>
                              <strong data-testid="payment-subtotal">{formatCurrency(eff)}</strong>
                            </div>
                            {surcharge > 0 && (
                              <div className="flex justify-between text-amber-800"
                                data-testid="cc-surcharge-line">
                                <span>Credit Card Processing Fee (3%):</span>
                                <strong data-testid="cc-surcharge-amount">{formatCurrency(surcharge)}</strong>
                              </div>
                            )}
                            <div className="flex justify-between pt-1 border-t border-amber-200">
                              <span className="font-semibold">Paying today:</span>
                              <strong data-testid="payment-total">{formatCurrency(totalCharge)}</strong>
                            </div>
                            {eff < proposal.balance_due && (
                              <div className="text-xs opacity-75 pt-1">
                                Remaining after: <strong>{formatCurrency(proposal.balance_due - eff)}</strong>
                              </div>
                            )}
                            {method === 'ach' && (
                              <div className="text-xs opacity-75 italic pt-1">
                                Tip: ACH payments do not incur the 3% processing fee.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })()}

                {/* Method tabs */}
                <div className="flex gap-2">
                  <button type="button"
                    onClick={() => setMethod('card')}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition ${
                      method === 'card' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="pay-method-card-tab">
                    <CreditCard className="w-4 h-4" />Credit / Debit Card
                  </button>
                  <button type="button"
                    onClick={() => setMethod('ach')}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition ${
                      method === 'ach' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="pay-method-ach-tab">
                    <Landmark className="w-4 h-4" />Bank Account (ACH)
                  </button>
                </div>

                {/* Common billing */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">First name</Label>
                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required data-testid="pay-first-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Last name</Label>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required data-testid="pay-last-name" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="pay-email" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Billing address</Label>
                    <Input value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required data-testid="pay-address" />
                  </div>
                  <div>
                    <Label className="text-xs">City</Label>
                    <Input value={city} onChange={(e) => setCity(e.target.value)} required data-testid="pay-city" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">State</Label>
                      <Input value={state} onChange={(e) => setState(e.target.value)} maxLength={2} required data-testid="pay-state" />
                    </div>
                    <div>
                      <Label className="text-xs">ZIP</Label>
                      <Input value={zip} onChange={(e) => setZip(e.target.value)} required data-testid="pay-zip" />
                    </div>
                  </div>
                </div>

                {/* Card form — always rendered so Collect.js iframes are not destroyed when switching to ACH and back */}
                <form onSubmit={handleCardSubmit} className="space-y-3"
                  data-testid="card-payment-form"
                  style={{ display: method === 'card' ? 'block' : 'none' }}>
                    <CardBrandStrip />
                    <div>
                      <Label className="text-xs">Card number</Label>
                      <div id="stares-card-num"
                        className="border border-gray-300 rounded-md bg-white"
                        style={{ height: 40 }} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Expiration</Label>
                        <div id="stares-card-exp"
                          className="border border-gray-300 rounded-md bg-white"
                          style={{ height: 40 }} />
                      </div>
                      <div>
                        <Label className="text-xs">CVV</Label>
                        <div id="stares-card-cvv"
                          className="border border-gray-300 rounded-md bg-white"
                          style={{ height: 40 }} />
                      </div>
                    </div>

                    {submitError && (
                      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                        {submitError}
                      </div>
                    )}

                    <Button
                      id="collect-submit-card"
                      type="submit"
                      disabled={submitting || !collectReady}
                      className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-6 text-lg font-semibold"
                      data-testid="card-submit-button"
                    >
                      {submitting ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>
                      ) : !collectReady ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Loading secure form...</>
                      ) : (
                        <><Lock className="w-5 h-5 mr-2" />Pay {formatCurrency(computeTotalCharge(computePayAmount(proposal, amountChoice), 'card'))}</>
                      )}
                    </Button>
                  </form>

                {/* ACH form */}
                {method === 'ach' && (
                  <form onSubmit={handleAchSubmit} className="space-y-3" data-testid="ach-payment-form">
                    <div>
                      <Label className="text-xs">Name on bank account</Label>
                      <Input value={achName} onChange={(e) => setAchName(e.target.value)} required data-testid="ach-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Routing number (9 digits)</Label>
                      <Input value={achRouting} onChange={(e) => setAchRouting(e.target.value.replace(/\D/g, ''))}
                        maxLength={9} required data-testid="ach-routing" />
                    </div>
                    <div>
                      <Label className="text-xs">Account number</Label>
                      <Input value={achAccount} onChange={(e) => setAchAccount(e.target.value.replace(/\D/g, ''))}
                        required data-testid="ach-account" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Account holder</Label>
                        <select className="w-full border border-gray-300 rounded-md h-10 px-3"
                          value={achHolderType} onChange={(e) => setAchHolderType(e.target.value)}
                          data-testid="ach-holder-type">
                          <option value="personal">Personal</option>
                          <option value="business">Business</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Account type</Label>
                        <select className="w-full border border-gray-300 rounded-md h-10 px-3"
                          value={achAcctType} onChange={(e) => setAchAcctType(e.target.value)}
                          data-testid="ach-account-type">
                          <option value="checking">Checking</option>
                          <option value="savings">Savings</option>
                        </select>
                      </div>
                    </div>

                    <label className="flex items-start gap-2 text-xs text-gray-700 bg-gray-50 p-3 rounded">
                      <input type="checkbox" checked={achAuthorized}
                        onChange={(e) => setAchAuthorized(e.target.checked)}
                        className="mt-0.5" data-testid="ach-authorize" />
                      <span>
                        I authorize {brandName()} to debit my bank account for {formatCurrency(computePayAmount(proposal, amountChoice))}.
                        I understand this authorization will remain in effect until I cancel it in writing.
                      </span>
                    </label>

                    {submitError && (
                      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">
                        {submitError}
                      </div>
                    )}

                    <Button type="submit" disabled={submitting}
                      className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white py-6 text-lg font-semibold"
                      data-testid="ach-submit-button">
                      {submitting ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>
                      ) : (
                        <><Lock className="w-5 h-5 mr-2" />Pay {formatCurrency(computePayAmount(proposal, amountChoice))} via ACH</>
                      )}
                    </Button>
                  </form>
                )}
              </>
            )}
          </div>

          <div className="bg-gray-50 border-t px-6 py-4 text-center">
            <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-1">
              <Lock className="w-3.5 h-3.5" />
              <span>Secured by Inspyre Payments · 256-bit SSL encryption</span>
            </div>
            <p className="text-sm text-gray-700">Questions? Contact us at <a href={`mailto:${brandEmail()}`} className="font-semibold underline">{brandEmail()}</a></p>
          </div>
        </div>
        <p className="text-center text-gray-500 text-xs mt-4">
          This quote is valid for 30 days from the date of issue.
        </p>
      </div>
    </div>
  );
};

export default PublicPayment;
